<template>
  <div class="group relative flex items-start space-x-3 p-4">
    <activity-action-menu v-if="actions.length" :actions :is-pinned />
    <div
      class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
      :class="iconContainer.class"
    >
      <icon-base
        :icon="iconContainer.icon"
        class="text-white"
        :width="12"
        :height="12"
        :view-box="iconContainer.iconViewBox"
      />
      <slot name="subIcon" />
    </div>
    <div class="flex w-full flex-col space-y-1-5 overflow-hidden">
      <slot name="content" />
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";

import {
  CallSessionStatus,
  CommunicationType,
} from "@/enums/communicationLogs";
import { SmsStatus, isSmsStatusFailed } from "@/enums/sms";
import { useEmailLogInfo } from "@/hooks/communicationLogs";

import ActivityActionMenu from "@/components/deals/dealActivity/ActivityActionMenu.vue";
import IconAINav from "@/components/icons/IconAINav.vue";
import IconMessage from "@/components/icons/IconMessage.vue";
import IconNotes from "@/components/icons/IconNotes.vue";
import IconTask from "@/components/icons/IconTask.vue";
import IconTelephone from "@/components/icons/IconTelephone.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { EmailLogData } from "@/models/communicationLogs";
import type { ActivityAction } from "@/models/dealActivity";

const {
  type,
  actions,
  isPinned = false,
  status,
  item,
  smsStatus,
} = defineProps<{
  type: CommunicationType;
  actions: ActivityAction[];
  isPinned?: boolean;
  status?: CallSessionStatus;
  item?: EmailLogData;
  smsStatus?: SmsStatus;
}>();

const isFailed = computed(() =>
  smsStatus ? isSmsStatusFailed(smsStatus) : false
);

const { iconInfo } = useEmailLogInfo(item);

const iconContainer = computed(() => {
  const options = {
    [CommunicationType.note]: {
      class: "relative bg-flush-orange",
      icon: IconNotes,
      viewBox: "0 0 16 16",
    },
    [CommunicationType.sms]: {
      class: isFailed.value ? "bg-error" : "bg-secondary-blue",
      icon: IconMessage,
      viewBox: "0 0 16 16",
    },
    [CommunicationType.call]: {
      class: [
        CallSessionStatus.Initiated,
        CallSessionStatus.Answered,
        CallSessionStatus.Ringing,
        CallSessionStatus.InProgress,
      ].some((callStatus) => callStatus === status)
        ? "bg-success-main"
        : "bg-error",
      icon: IconTelephone,
      viewBox: "0 0 16 16",
    },
    [CommunicationType.task]: {
      class: "bg-secondary-purple-1",
      icon: IconTask,
      viewBox: "0 0 12 12",
    },
    [CommunicationType.email]: {
      class: iconInfo.value.backgroundClass,
      icon: iconInfo.value.icon,
      viewBox: iconInfo.value.viewBox,
    },
    [CommunicationType.ai_agent]: {
      class: "bg-secondary-purple",
      icon: IconAINav,
      viewBox: "0 0 24 24",
    },
  };

  return {
    class: options[type].class,
    icon: options[type].icon,
    iconViewBox: options[type].viewBox,
  };
});
</script>
